import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useRouter} from '@uirouter/react'
import _isEqual from 'lodash.isequal'

import {useSelector} from 'react-redux'
import {
  select_gridSession,
  select_gridSession_pageCount,
  select_gridSession_resultsCount,
  select_gridSession_pageSizeOptions,
  select_gridSession_sorting,
  select_jobs,
  select_jobs_showDetails,
  select_status_isPending,
} from 'redux/jobs/selectors'
import {useAppDispatch} from 'redux/config'

import {refreshJobs} from 'redux/jobs/jobs.duck'
import {EmptyStateProps} from 'src/components/EmptyState/index'

import {DataGrid} from '@mdsol/onex-design/DataGrid'

import columnsConfig from './columnsConfig'
import {SortingState} from '@tanstack/react-table'

import GridControl from './GridControl'
import JobDetails from './JobDetails'

import 'components/common/grid/grid.scss'
import './index.scss'
import {select_auth_user} from 'redux/auth/selectors'
import {select_jobTypeActionOptionsStatus} from '../../../redux/jobs/selectors'
import {fetchJobTypeActionOptions} from '../../../redux/jobs/jobs.duck'
import {Status} from '../../../commonTypes'

const Jobs: FC = () => {
  const dispatch = useAppDispatch()
  const router = useRouter()

  const displayRowDetails = useSelector(select_jobs_showDetails)
  const {isAdmin, isPowerUser} = useSelector(select_auth_user)
  const jobs = useSelector(select_jobs)
  const gridSession = useSelector(select_gridSession)
  const pageCount = useSelector(select_gridSession_pageCount)
  const resultsCount = useSelector(select_gridSession_resultsCount)
  const pageSizeOptions = useSelector(select_gridSession_pageSizeOptions)
  const sorting = useSelector(select_gridSession_sorting)
  const isLoading = useSelector(select_status_isPending)

  const emptyStateOptions = jobs.length
    ? null
    : {
      title: 'No data',
      subtitle: 'No jobs are available.',
      defaultImgVariant: 'noData',
    } as EmptyStateProps

  const [{pageIndex, pageSize}, setPagination] = useState({
    pageIndex: gridSession.pagination.pageIndex,
    pageSize: gridSession.pagination.pageSize,
  })

  const pagination = useMemo(() => ({
    pageIndex,
    pageSize,
  }),
  [pageIndex, pageSize]
  )

  const state = useMemo(() => ({
    sorting: sorting,
    columnVisibility: {
      selectJobRow: isAdmin || isPowerUser,
    },
  }),
  [sorting, isAdmin, isPowerUser]
  )

  const jobTypeActionOptionsStatus = useSelector(select_jobTypeActionOptionsStatus)

  useEffect(() => {
    dispatch(fetchJobTypeActionOptions())
  }, [dispatch])

  useEffect(() => {
    dispatch(refreshJobs(router, pagination, undefined, undefined))
  }, [pagination, dispatch, router])

  const handleChangeControl = useCallback(tableState => {

    const sortingState = tableState.sorting as SortingState
    if (sortingState) {
      const hasSortingChanged = !_isEqual(sorting, sortingState)
      if (hasSortingChanged) {
        dispatch(refreshJobs(router, undefined, sortingState, undefined))
      }
    }

  }, [sorting, dispatch, router])

  return (
    <>
      {jobTypeActionOptionsStatus === Status.OK &&
        <div id='grid-container' className={`${displayRowDetails ? 'with-row-details' : ''}`}>
          <div className='main-content'>
            <DataGrid
              dataTestId='jobs-data-grid'
              customGridControl={<GridControl key='key-jobs-grid-control' />}
              isLoading={isLoading}
              manualDataLength={resultsCount}
              state={state}
              columns={columnsConfig}
              data={jobs}
              manualSorting
              enableSorting
              onChangeControl={handleChangeControl}
              enablePagination
              pagination={pagination}
              rowsPerPageOptions={[...pageSizeOptions]}
              pageCount={pageCount}
              onPaginationChange={setPagination}
              emptyStateOptions={emptyStateOptions}
            />
          </div>
          {displayRowDetails && (
            <div className='details-panel'>
              <JobDetails />
            </div>
          )}
        </div>
      }
    </>
  )
}

export default Jobs
